export function isDateOnlyString(value) {
    return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)
}

export function isNaiveDateTimeString(value) {
    // Matches:
    // - "YYYY-MM-DD HH:mm"
    // - "YYYY-MM-DD HH:mm:ss"
    // - "YYYY-MM-DDTHH:mm"
    // - "YYYY-MM-DDTHH:mm:ss"
    // But NOT timezone-stamped values like "...Z" or "...+05:00"
    if (typeof value !== "string") return false
    if (!/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2})?/.test(value)) return false
    return !/(Z|[+-]\d{2}:?\d{2})$/.test(value)
}

// CRA inlines `process.env.REACT_APP_*` at build time. Do not use optional
// chaining on `process` — webpack won't replace it and the browser throws
// "process is not defined" at runtime.
export function getAppTimeZone() {
    return process.env.REACT_APP_TIMEZONE || "America/New_York"
}

function pickPart(parts, type) {
    const match = parts.find((p) => p.type === type)
    return match?.value ?? null
}

// Extract { y, m, d } for the given value in a specific IANA timezone.
// IMPORTANT: date-only strings and naive datetimes are treated as calendar
// dates (no timezone shift). Only real instants (Date / ISO-with-offset/Z)
// are converted using `Intl` into the target timezone.
export function getDatePartsInTimeZone(value, timeZone = getAppTimeZone()) {
    if (!value || value === "0000-00-00" || value === "0000-00-00 00:00:00") return null

    // Date-only values are already calendar dates; do not shift them.
    if (typeof value === "string" && isDateOnlyString(value)) {
        const [y, m, d] = value.split("-").map(Number)
        if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null
        return { y, m, d }
    }

    // Naive datetimes are also treated as calendar date + local wall time; for
    // day-diff logic we only care about the date portion.
    if (typeof value === "string" && isNaiveDateTimeString(value)) {
        const datePart = value.replace("T", " ").split(" ")[0]
        if (!isDateOnlyString(datePart)) return null
        const [y, m, d] = datePart.split("-").map(Number)
        if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null
        return { y, m, d }
    }

    const d = parseApiDateOrDateTime(value)
    if (!d) return null

    try {
        const fmt = new Intl.DateTimeFormat("en-US", {
            timeZone,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        })
        const parts = fmt.formatToParts(d)
        const y = Number(pickPart(parts, "year"))
        const m = Number(pickPart(parts, "month"))
        const day = Number(pickPart(parts, "day"))
        if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(day)) return null
        return { y, m, d: day }
    } catch {
        // If an invalid timezone is configured, fall back to the local calendar.
        const y = d.getFullYear()
        const m = d.getMonth() + 1
        const day = d.getDate()
        return { y, m, d: day }
    }
}

export function parseApiDateOrDateTime(value) {
    if (!value || value === "0000-00-00" || value === "0000-00-00 00:00:00") return null
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value

    if (typeof value === "string") {
        if (isDateOnlyString(value)) {
            const [y, m, d] = value.split("-").map(Number)
            const date = new Date(y, (m || 1) - 1, d || 1)
            return Number.isNaN(date.getTime()) ? null : date
        }

        if (isNaiveDateTimeString(value)) {
            const normalized = value.replace("T", " ")
            const [datePart, timePartRaw] = normalized.split(" ")
            const [y, m, d] = datePart.split("-").map(Number)
            const timePart = timePartRaw || "00:00:00"
            const [hh, mm, ss] = timePart.split(":").map((v) => Number(v ?? 0))
            const date = new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, ss || 0)
            return Number.isNaN(date.getTime()) ? null : date
        }

        // ISO with timezone (Z / +/- offset) or other supported browser formats.
        const parsed = new Date(value)
        return Number.isNaN(parsed.getTime()) ? null : parsed
    }

    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : parsed
}

export function formatApiDateEnUs(value) {
    if (!value) return value

    if (typeof value === "string") {
        if (isDateOnlyString(value)) {
            const [y, m, d] = value.split("-")
            return `${m}/${d}/${y}`
        }
        // If it's a datetime string, format just the date part.
        const datePart = value.split(" ")[0]?.split("T")[0]
        if (isDateOnlyString(datePart)) {
            const [y, m, d] = datePart.split("-")
            return `${m}/${d}/${y}`
        }
    }

    const d = parseApiDateOrDateTime(value)
    if (!d) return value
    const mm = String(d.getMonth() + 1).padStart(2, "0")
    const dd = String(d.getDate()).padStart(2, "0")
    const yyyy = d.getFullYear()
    return `${mm}/${dd}/${yyyy}`
}

// Converts a date value to "YYYY-MM-DD" for sending to the API. Accepts
// already-normalized date-only strings, "m/d/Y" / "m-d-Y" inputs (e.g. from
// the police report moonbird data), and anything parseApiDateOrDateTime can
// handle. Returns the original value if it can't be interpreted as a date.
export function toApiDateYmd(value) {
    if (!value) return value

    if (typeof value === "string") {
        const trimmed = value.trim()

        if (isDateOnlyString(trimmed)) return trimmed

        // Y-m-d or Y/m/d (tolerates single-digit month/day, e.g. "1990-5-3").
        const ymd = trimmed.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/)
        if (ymd) {
            const [, y, m, d] = ymd
            return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`
        }

        // m/d/Y or m-d-Y (also tolerates single-digit month/day).
        const mdy = trimmed.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/)
        if (mdy) {
            const [, m, d, y] = mdy
            return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`
        }
    }

    const d = parseApiDateOrDateTime(value)
    if (!d) return value
    const yyyy = d.getFullYear()
    const mm = String(d.getMonth() + 1).padStart(2, "0")
    const dd = String(d.getDate()).padStart(2, "0")
    return `${yyyy}-${mm}-${dd}`
}

export function formatApiDateTimeEnUsNoSeconds(value) {
    if (!value) return value

    const d = parseApiDateOrDateTime(value)
    if (!d) return value

    const mm = d.getMonth() + 1
    const dd = d.getDate()
    const yyyy = d.getFullYear()

    let hour = d.getHours()
    const minute = String(d.getMinutes()).padStart(2, "0")
    const ampm = hour >= 12 ? "PM" : "AM"
    hour = hour % 12
    if (hour === 0) hour = 12

    return `${mm}/${dd}/${yyyy} ${hour}:${minute} ${ampm}`
}

// Returns "YYYY-MM-DD" using the APP timezone calendar (never UTC). Defaults
// to America/New_York when REACT_APP_TIMEZONE is not set.
export function localDateOnlyString(date = new Date()) {
    const parts = getDatePartsInTimeZone(date, getAppTimeZone())
    if (!parts) return null
    const mm = String(parts.m).padStart(2, "0")
    const dd = String(parts.d).padStart(2, "0")
    return `${parts.y}-${mm}-${dd}`
}

// Calendar-day difference (to - from) in the APP timezone.
export function diffCalendarDays(from, to = new Date()) {
    const tz = getAppTimeZone()
    const fromParts = getDatePartsInTimeZone(from, tz)
    const toParts = getDatePartsInTimeZone(to, tz)
    if (!fromParts || !toParts) return null
    const fromUtc = Date.UTC(fromParts.y, fromParts.m - 1, fromParts.d)
    const toUtc = Date.UTC(toParts.y, toParts.m - 1, toParts.d)
    return Math.round((toUtc - fromUtc) / (1000 * 60 * 60 * 24))
}

